import { useState, useEffect, useRef, useMemo, Children } from 'react';
import {
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Drawer,
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Chip,
  ListItemButton,
  Button,
  Card as MuiCard,
  CardContent,
  Stack,
  Avatar as MuiAvatar,
  SvgIcon,
  LinearProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faUsers,
  faClock,
  faPlane,
  faBriefcase,
  faChartLine,
  faWallet,
  faFileAlt,
  faChartBar,
  faCog,
  faBell,
  faSearch,
  faChevronRight,
  faRobot,
  faPaperPlane,
  faTimes,
  faPlus,
  faCheck,
  faBuilding,
  faAward,
  faSignOutAlt,
  faBook,
  faShieldAlt,
  faSparkles,
  faArrowRight,
  faArrowLeft,
  faDownload,
  faUser,
  faStar,
  faMapPin,
  faEnvelope,
  faPhone,
  faPencil,
  faAlertTriangle,
  faHashtag,
  faCalendar,
  faGraduationCap,
  faUserPlus,
  faComment,
  faBars,
} from '@fortawesome/free-solid-svg-icons';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import {
  Wallet,
  Users,
  ShieldCheck,
  Plus,
  Check,
  ArrowLeft,
  ArrowRight,
  Mail,
  Network,
  Target,
  Megaphone,
  HardHat,
  HeartPulse,
  UserMinus,
  Gavel,
  GitBranch,
  Banknote,
  CalendarRange,
  Phone,
  MapPin,
  Briefcase,
  Building2,
  GraduationCap,
  Clock,
  X,
  CalendarOff,
  CircleUser,
  Sparkles,
  TrendingUp,
  TrendingDown,
  LayoutDashboard,
  FileText,
  BarChart3,
  Home,
  BookOpen,
  UserPlus,
  AlertTriangle,
  Settings,
  Search,
  Bell,
  Send,
  Hash,
  Calendar,
  Pencil,
  CornerDownLeft,
  ChevronRight,
  MessageSquare,
  LogOut,
  Bot,
  Download,
  Star,
  PanelLeft,
  UserCog,
  SlidersHorizontal,
  Award,
  Trophy,
  Activity,
  Building,
  Inbox,
  Menu as MenuIcon,
} from 'lucide-react';
import { api, getEntity, setEntity } from './api';
import EmployeesDataGrid from './EmployeesDataGrid';
import AppDataGrid, {
  DataGridCard,
  DataGridPage,
  PersonCell,
  TextCell,
  GridStatusPill,
  useAttendanceColumns,
  useHistoryColumns,
} from './AppDataGrid';
import { useAuth } from './auth';
import { useAppSettings } from './SettingsContext';
import { buildPath, parsePath } from './routing';
import {
  StatusPill,
  UserAvatar,
  Header,
  SectionHeader,
  GridLayout,
  EmptyState,
} from './components';
import { AG, PIE_COLORS, STATUS_COLOR, statusTone, syncTokens } from './theme/tokens';
import { BRANDS, getPieColors, isDihBrand } from './appSettings';
import { initials, empEmail, tenure, fmtPKR, fmtM, greeting } from './lib/format';
import { STAGES, JOB_STAGES, jobTone, jobName } from './lib/ats';
import { printPayDoc, docRow, printReport } from './lib/print';
import {
  OKR_RATINGS,
  RATING_NUM,
  ratingTone,
  okrStatusTone,
  okrTabBtn,
  okrLbl,
  weightedProgress,
  avgRatingLabel,
  okrsByEmployee,
} from './lib/okr';
import {
  Pill,
  Avatar,
  AccountButton,
  Card,
  DashboardWidget,
  WidgetGrid,
  KPI,
  CardSectionHeader,
  ChartCard,
  SectionTitle,
  ChartTip,
  CenterSpinner,
  PrimaryBtn,
  OutlineBtn,
  PageScroll,
  PTh,
  PTd,
  PerfTable,
  DrawerFormRow,
  DrawerSection,
  DrawerTextField,
  DRAWER_TABLE_SX,
} from './ui';
import { CommandPalette } from './CommandPalette';
import { ChatWidget } from './ChatWidget';
import { EntitySwitcher } from './EntitySwitcher';
import { AdminViews, EmployeeDetail, CandidateDetail, JobDetail } from './adminViews';
import { EmployeeViews } from './employeeViews';

// Header bell: actionable in-app notifications routed to this user's bucket.
function NotificationBell({ onNavigate }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = () =>
    api.notifications().then((d) => { setItems(d.items || []); setUnread(d.unread || 0); }).catch(() => {});
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    const iv = setInterval(load, 60000); // light poll so approvals surface without a refresh
    return () => { window.removeEventListener('focus', onFocus); clearInterval(iv); };
  }, []);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const clickItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      setItems((p) => p.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      api.markNotifRead(n.id).catch(() => {});
    }
    if (n.link) onNavigate?.(n.link);
  };
  const markAll = () => {
    setItems((p) => p.map((x) => ({ ...x, read: true })));
    setUnread(0);
    api.markAllNotifRead().catch(() => {});
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="ag-header-icon-btn relative navlift flex items-center justify-center"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
      >
        <Bell size={20} />
        {unread > 0 && <span className="ag-notif-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <div className="ag-notif-panel">
          <div className="ag-notif-head">
            <span>Notifications</span>
            {unread > 0 && <button onClick={markAll} className="ag-notif-readall">Mark all read</button>}
          </div>
          <div className="ag-notif-list">
            {items.length === 0 ? (
              <div className="ag-notif-empty">You’re all caught up 🎉</div>
            ) : (
              items.map((n) => (
                <button key={n.id} onClick={() => clickItem(n)} className={`ag-notif-item${n.read ? '' : ' unread'}`}>
                  <span className={`ag-notif-type ag-notif-${n.type}`} />
                  <span className="ag-notif-msg">{n.message}</span>
                  {!n.read && <span className="ag-notif-dot-unread" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AugmentHR() {
  const { user, logout, isHR, isManager } = useAuth();
  const { tokens, openDrawer, settings } = useAppSettings();
  syncTokens(tokens, getPieColors(settings));
  const isDih = isDihBrand(settings);
  const brandConfig = BRANDS[isDih ? 'dih' : 'augment'];
  // HR is also an employee. "My Workspace" flips HR into their own self-service
  // (own leave / attendance / payslips / performance) without leaving the app.
  const [hrSelf, setHrSelf] = useState(false);
  const adminMode = isHR && !hrSelf; // render the admin UI vs employee self-service

  // Initial view + detail come from the URL, so a refresh or shared deep link
  // lands on the right tab instead of always resetting to the Dashboard.
  const initialRoute = useRef(parsePath(window.location.pathname)).current;
  const [navAdmin, setNavAdmin] = useState(initialRoute.view);
  const [navEmp, setNavEmp] = useState(initialRoute.view);
  const [navSignal, setNavSignal] = useState(0);
  const [toast, setToast] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatSeed, setChatSeed] = useState(null);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [cmdQ, setCmdQ] = useState('');
  const searchBtnRef = useRef(null);
  const cmdInputRef = useRef(null);
  const cmdWrapRef = useRef(null);
  const closeCmd = () => { setCmdOpen(false); setCmdQ(''); };
  const [detail, setDetail] = useState(initialRoute.detail);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [navHovered, setNavHovered] = useState(false);
  const navHoverTimer = useRef(null);
  // Mobile: the sidebar becomes an off-canvas drawer toggled by a hamburger.
  const isMobile = useMediaQuery('(max-width: 900px)');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    if (!isMobile) setMobileNavOpen(false);
  }, [isMobile]);

  const onSidebarEnter = () => {
    if (!navCollapsed) return;
    clearTimeout(navHoverTimer.current);
    navHoverTimer.current = setTimeout(() => setNavHovered(true), 0);
  };
  const onSidebarLeave = () => {
    if (!navCollapsed) return;
    clearTimeout(navHoverTimer.current);
    navHoverTimer.current = setTimeout(() => setNavHovered(false), 400);
  };
  const sidebarFlyout = navCollapsed && navHovered;

  // Shared mutable collections
  const [employees, setEmployees] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [balances, setBalances] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [offers, setOffers] = useState([]);
  const [onboarding, setOnboarding] = useState([]);
  const [atsTab, setAtsTab] = useState('Overview');
  const [viewLoading, setViewLoading] = useState(true);
  // Entity scope (HR can switch; "all" = consolidated). Persisted via api.js so
  // every request carries the X-Entity header. `entityId` also keys the view
  // subtree so a switch remounts (and refetches) it.
  const [orgTree, setOrgTree] = useState([]);
  const [entityId, setEntityId] = useState(getEntity());

  const showToast = (msg, type) => {
    // Infer tone from the message when a caller doesn't pass one explicitly.
    const t =
      type ||
      (/(fail|error|not found|already|invalid|must |cannot|could not|forbidden|unable|denied|wrong|over your)/i.test(
        String(msg),
      )
        ? 'error'
        : 'success');
    setToast({ msg, type: t });
    setTimeout(() => setToast(null), 3200);
  };

  // ---- lazy, per-tab data loading ----
  // Each collection is fetched only the first time a tab that needs it is
  // opened, then cached — so we no longer fire a burst of calls on login.
  const loaded = useRef({});

  const RESOURCE_LOADERS = {
    employees: () => api.employees().then(setEmployees),
    candidates: () => api.candidates().then(setCandidates),
    leaves: () => api.leaves().then(setLeaves),
    balances: () => api.leaveBalances().then(setBalances),
    jobs: () => api.jobs().then(setJobs),
    interviews: () => api.interviews().then(setInterviews),
    offers: () => api.offers().then(setOffers),
    onboarding: () => api.onboarding().then(setOnboarding),
  };

  // Top-level data each tab needs. Tabs not listed either fetch their own data
  // on mount (Attendance, Performance, Payroll, Policies, Reports) or are static.
  const VIEW_RESOURCES = {
    // Admin & employee share these view names now; union of what each needs.
    Dashboard: ['leaves', 'balances'],
    Employees: ['employees'],
    Leave: ['leaves', 'balances'],
    Recruitment: ['jobs', 'candidates', 'interviews', 'offers', 'onboarding'],
  };

  // Fetch the given resources once; cache on success, allow retry on failure.
  const ensure = (names = []) => {
    const todo = names.filter((n) => !loaded.current[n]);
    todo.forEach((n) => {
      loaded.current[n] = true;
    });
    return Promise.all(
      todo.map((n) =>
        RESOURCE_LOADERS[n]().catch((err) => {
          loaded.current[n] = false;
          throw err;
        }),
      ),
    );
  };

  const currentView = adminMode ? navAdmin : navEmp;

  // ---- URL routing ----
  // Push the URL whenever the active view or detail changes, so each tab has its
  // own address. Guarded so it never loops with the popstate handler below.
  useEffect(() => {
    const path = buildPath(currentView, detail);
    if (path !== window.location.pathname) {
      window.history.pushState(null, '', path);
    }
  }, [currentView, detail]);

  // Browser back/forward (and manual URL edits): mirror the URL back into state.
  useEffect(() => {
    const onPop = () => {
      const r = parsePath(window.location.pathname);
      if (adminMode) setNavAdmin(r.view);
      else setNavEmp(r.view);
      setDetail(r.detail);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [adminMode]);

  // Load the active tab's data when it changes (cached → revisits are instant).
  useEffect(() => {
    let active = true;
    const needed = (VIEW_RESOURCES[currentView] || []).filter(
      (n) => !loaded.current[n],
    );
    if (needed.length === 0) {
      setViewLoading(false);
      return;
    }
    setViewLoading(true);
    ensure(needed)
      .then(() => {
        if (active) setViewLoading(false);
      })
      .catch((e) => {
        if (active) {
          setViewLoading(false);
          showToast(e.message || 'Failed to load data');
        }
      });
    return () => {
      active = false;
    };
  }, [currentView]);

  // The command palette (⌘K) searches people — load them when it first opens.
  useEffect(() => {
    if (cmdOpen && isHR) ensure(['employees', 'candidates']).catch(() => {});
  }, [cmdOpen]);

  // Load the Company → Group → Entity tree for the HR entity switcher.
  useEffect(() => {
    if (isHR) api.orgTree().then(setOrgTree).catch(() => {});
  }, [isHR]);

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdOpen((o) => {
          if (!o) setTimeout(() => cmdInputRef.current?.focus(), 0);
          return !o;
        });
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);
  // Clear the search query whenever the palette closes (any close path)
  useEffect(() => { if (!cmdOpen) setCmdQ(''); }, [cmdOpen]);
  // Close the command palette when clicking anywhere outside the search bar + dropdown
  useEffect(() => {
    if (!cmdOpen) return;
    const onDocPointerDown = (e) => {
      if (cmdWrapRef.current && !cmdWrapRef.current.contains(e.target)) closeCmd();
    };
    document.addEventListener('mousedown', onDocPointerDown);
    return () => document.removeEventListener('mousedown', onDocPointerDown);
  }, [cmdOpen]);

  // ---- handlers (call API, then update local state) ----
  const moveCandidate = async (id, dir) => {
    try {
      const c = await api.moveCandidate(id, dir);
      setCandidates((prev) => prev.map((x) => (x.id === id ? c : x)));
      showToast(`${c.name} moved to ${c.stage}`);
    } catch (e) {
      showToast(e.message);
    }
  };
  const submitLeave = async (form) => {
    try {
      const lv = await api.applyLeave(form);
      setLeaves((prev) => [lv, ...prev]);
      showToast('Leave application submitted — pending approval');
    } catch (e) {
      showToast(e.message);
    }
  };
  const decideLeave = async (id, decision) => {
    try {
      const lv = await api.decideLeave(id, decision);
      setLeaves((prev) => prev.map((l) => (l.id === id ? lv : l)));
      showToast(`Leave ${id} ${decision.toLowerCase()}`);
    } catch (e) {
      showToast(e.message);
    }
  };
  const cancelLeave = async (id) => {
    try {
      const lv = await api.cancelLeave(id);
      setLeaves((prev) => prev.map((l) => (l.id === id ? lv : l)));
      api.leaveBalances().then(setBalances).catch(() => {}); // approved cancel restores balance
      showToast('Leave cancelled');
    } catch (e) {
      showToast(e.message);
    }
  };

  const navTo = (view) => {
    if (adminMode) setNavAdmin(view);
    else setNavEmp(view);
    setDetail(null);
    setCmdOpen(false);
  };
  // HR: switch the active entity. Persist it (api.js sends X-Entity), drop the
  // lazy cache, refetch the current view's shell data, and bump entityId — which
  // keys <AdminViews>/<EmployeeViews> so views that fetch on mount reload too.
  const changeEntity = (id) => {
    const next = id || 'all';
    if (next === entityId) return;
    setEntity(next);
    setEntityId(next);
    setDetail(null);
    loaded.current = {};
    ensure(VIEW_RESOURCES[currentView] || []).catch((e) => showToast(e.message || 'Failed to load data'));
  };
  // Re-fetch the org tree (after HR creates a group/entity) so the header
  // switcher and the add-employee entity picker reflect the change immediately.
  const refreshOrg = () => api.orgTree().then(setOrgTree).catch(() => {});
  // HR: switch between admin and personal self-service. Reset to Dashboard so we
  // never land on a view key that only exists in the other mode.
  const switchWorkspace = (toSelf) => {
    if (toSelf === hrSelf) return;
    setDetail(null);
    setHrSelf(toSelf);
    if (toSelf) setNavEmp('Dashboard');
    else setNavAdmin('Dashboard');
  };
  const openEmployee = (id) => {
    setDetail({ type: 'employee', id });
    setCmdOpen(false);
  };
  const openCandidate = (id) => {
    setDetail({ type: 'candidate', id });
    setCmdOpen(false);
  };
  const openJob = (id) => {
    setDetail({ type: 'job', id });
    setCmdOpen(false);
  };

  // Employee directory: add / edit (throw on error so the modal can show it)
  const addEmployee = async (data) => {
    const e = await api.addEmployee(data);
    setEmployees((prev) => [e, ...prev]);
    showToast(`${e.name} added to the directory`);
    return e;
  };
  const updateEmployee = async (id, data) => {
    const e = await api.updateEmployee(id, data);
    setEmployees((prev) => prev.map((x) => (x.id === id ? { ...x, ...e } : x)));
    showToast(`${e.name}'s profile updated`);
    return e;
  };
  const disableEmployee = async (id, lastDay) => {
    const e = await api.updateEmployee(id, { status: 'Inactive', lastDay });
    setEmployees((prev) => prev.map((x) => (x.id === id ? { ...x, ...e } : x)));
    showToast(`${e.name} deactivated · last day ${lastDay}`);
    return e;
  };
  const restoreEmployee = async (id, reason) => {
    const e = await api.updateEmployee(id, {
      status: 'Active',
      lastDay: null,
      ...(reason ? { reactivationReason: reason } : {}),
    });
    setEmployees((prev) => prev.map((x) => (x.id === id ? { ...x, ...e } : x)));
    showToast(`${e.name} reactivated${reason ? ` — ${reason}` : ''}`);
    return e;
  };
  const refreshEmployees = () => api.employees().then(setEmployees);
  const promoteToManager = async (id) => {
    const e = await api.promoteManager(id);
    await refreshEmployees();
    showToast(
      `${e.name} is now a manager${e.removedFromTeam ? ` — removed from ${e.formerManager}'s team` : ''}`,
    );
    return e;
  };
  const removeAsManager = async (id, newManagerId) => {
    const e = await api.removeManager(id, newManagerId);
    await refreshEmployees();
    showToast(
      e.reassigned
        ? `${e.name} removed as manager · ${e.reassigned} report(s) moved to ${e.newManager}`
        : `${e.name} is no longer a manager`,
    );
    return e;
  };

  // ATS handlers
  const advanceJob = async (id) => {
    try {
      const j = await api.advanceJob(id);
      setJobs((prev) => prev.map((x) => (x.id === id ? j : x)));
      showToast(`${j.title} → ${j.status}`);
    } catch (e) {
      showToast(e.message);
    }
  };
  const closeJob = async (id) => {
    try {
      const j = await api.closeJob(id);
      setJobs((prev) => prev.map((x) => (x.id === id ? j : x)));
      showToast('Requisition closed');
    } catch (e) {
      showToast(e.message);
    }
  };
  const addJob = async (job) => {
    try {
      const j = await api.addJob(job);
      setJobs((prev) => [j, ...prev]);
      showToast(`Requisition "${j.title}" created (Draft)`);
    } catch (e) {
      showToast(e.message);
    }
  };
  const rejectCandidate = async (id) => {
    try {
      const c = await api.rejectCandidate(id);
      setCandidates((prev) => prev.map((x) => (x.id === id ? c : x)));
      showToast('Candidate moved to rejected');
    } catch (e) {
      showToast(e.message);
    }
  };
  const restoreCandidate = async (id) => {
    try {
      const c = await api.restoreCandidate(id);
      setCandidates((prev) => prev.map((x) => (x.id === id ? c : x)));
      showToast('Candidate restored to pipeline');
    } catch (e) {
      showToast(e.message);
    }
  };
  const scheduleInterview = async (iv) => {
    try {
      const v = await api.scheduleInterview(iv);
      setInterviews((prev) => [v, ...prev]);
      showToast('Interview scheduled');
    } catch (e) {
      showToast(e.message);
    }
  };
  const completeInterview = async (id, rec) => {
    try {
      const v = await api.completeInterview(id, rec);
      setInterviews((prev) => prev.map((x) => (x.id === id ? v : x)));
      showToast(`Interview marked complete — ${rec}`);
    } catch (e) {
      showToast(e.message);
    }
  };
  const createOffer = async (candId, salary, start) => {
    try {
      const o = await api.createOffer(candId, salary, start);
      setOffers((prev) => [o, ...prev]);
      showToast('Offer drafted');
    } catch (e) {
      showToast(e.message);
    }
  };
  const advanceOffer = async (id) => {
    try {
      const o = await api.advanceOffer(id);
      setOffers((prev) => prev.map((x) => (x.id === id ? o : x)));
      showToast(`Offer ${o.status.toLowerCase()}`);
    } catch (e) {
      showToast(e.message);
    }
  };
  const decideOffer = async (id, decision) => {
    try {
      const o = await api.decideOffer(id, decision);
      setOffers((prev) => prev.map((x) => (x.id === id ? o : x)));
      if (decision === 'Accepted') {
        const [cand, ob] = await Promise.all([
          api.candidates(),
          api.onboarding(),
        ]);
        setCandidates(cand);
        setOnboarding(ob);
        showToast('Offer accepted — onboarding started');
      } else showToast('Offer declined');
    } catch (e) {
      showToast(e.message);
    }
  };
  const toggleOnboardTask = async (obId, taskId) => {
    try {
      const ob = await api.toggleOnboardTask(obId, taskId);
      setOnboarding((prev) => prev.map((x) => (x.id === obId ? ob : x)));
    } catch (e) {
      showToast(e.message);
    }
  };
  const convertToEmployee = async (obId) => {
    try {
      const ob = await api.convertToEmployee(obId);
      setOnboarding((prev) => prev.map((x) => (x.id === obId ? ob : x)));
      showToast('New hire handed off to Core HR');
    } catch (e) {
      showToast(e.message);
    }
  };

  const doAction = (key) => {
    setCmdOpen(false);
    setDetail(null);
    const map = adminMode
      ? {
          'run-payroll': () => navTo('Payroll'),
          'add-employee': () => navTo('Employees'),
          'new-job': () => navTo('Recruitment'),
        }
      : {
          'apply-leave': () => navTo('Leave'),
          clock: () => navTo('Attendance'),
        };
    map[key]?.();
  };
  const askAI = (q) => {
    setCmdOpen(false);
    setChatOpen(true);
    setChatSeed(q);
  };

  const adminGroups = [
    { items: [{ name: 'Dashboard', icon: LayoutDashboard }] },
    {
      label: 'People',
      items: [
        { name: 'Employees', icon: Users },
        { name: 'Attendance', icon: Clock },
        { name: 'Leave', label: 'Leaves', icon: CalendarOff },
      ],
    },
    {
      label: 'Talent',
      items: [
        { name: 'Recruitment', icon: Briefcase },
        { name: 'JD Library', icon: FileText },
        { name: 'Performance', icon: TrendingUp },
      ],
    },
    {
      label: 'Operations',
      items: [
        { name: 'Insights', icon: BarChart3 },
        { name: 'Succession', icon: GitBranch },
        { name: 'Announcements', icon: Megaphone },
        { name: 'Approvals', icon: Inbox },
        { name: 'Payroll', icon: Wallet },
        { name: 'Benefits', icon: HeartPulse },
        { name: 'Loans', icon: Banknote },
        { name: 'Letters', icon: Mail },
        { name: 'Training', icon: GraduationCap },
        { name: 'Competencies', icon: Target },
        { name: '360 Feedback', icon: MessageSquare },
        { name: 'Recognition', icon: Award },
        { name: 'Awards', icon: Trophy },
        { name: 'Pulse', icon: BarChart3 },
        { name: 'Employee Relations', icon: ShieldCheck },
        { name: 'Health & Safety', icon: HardHat },
        { name: 'Disciplinary', icon: Gavel },
        { name: 'Policies', icon: FileText },
        { name: 'Organization Docs', icon: Building2 },
        { name: 'Reports', icon: BarChart3 },
        { name: 'Activity Log', icon: Activity },
      ],
    },
    {
      label: 'Organization',
      items: [
        { name: 'Entities', icon: Building },
        { name: 'Org Chart', icon: Network },
        { name: 'Performance Cycles', icon: CalendarRange },
        { name: 'Offboarding', icon: UserMinus },
        { name: 'Configuration', icon: SlidersHorizontal },
      ],
    },
  ];
  const empGroups = [
    { items: [{ name: 'Dashboard', icon: Home }] },
    {
      items: [
        { name: 'Attendance', icon: Clock },
        { name: 'Leave', label: 'Leaves', icon: CalendarOff },
        { name: 'Payroll', icon: Wallet },
        { name: 'My Benefits', icon: HeartPulse },
        { name: 'My Loans', icon: Banknote },
        { name: 'Performance', icon: TrendingUp },
        { name: 'Feedback', icon: MessageSquare },
        { name: 'Requests', icon: Send },
      ],
    },
    // Team section — only for managers (people with direct reports)
    ...(isManager
      ? [
          {
            label: 'Team',
            items: [
              { name: 'Team Analytics', icon: BarChart3 },
              { name: 'Approvals', icon: Inbox },
              { name: "Team's Attendance", icon: Users },
              { name: "Team's Leaves", icon: CalendarOff },
              { name: "Team's Performance", icon: TrendingUp },
            ],
          },
        ]
      : []),
    {
      label: 'Company',
      items: [
        { name: 'Announcements', icon: Megaphone },
        { name: 'My Profile', icon: CircleUser },
        { name: 'My Learning', icon: GraduationCap },
        { name: 'My Skills', icon: Target },
        { name: 'Recognition', icon: Award },
        { name: 'Awards', icon: Trophy },
        { name: 'Pulse', icon: BarChart3 },
        { name: 'Employee Relations', icon: ShieldCheck },
        { name: 'Health & Safety', icon: HardHat },
        { name: 'My Record', icon: Gavel },
        { name: 'Letters', icon: Mail },
        { name: 'Policies', icon: BookOpen },
        { name: 'Organization Docs', icon: Building2 },
        { name: 'Documents', label: 'My Documents', icon: FileText },
      ],
    },
  ];
  const groups = adminMode ? adminGroups : empGroups;
  const active = adminMode ? navAdmin : navEmp;
  const setActive = (n) => {
    setDetail(null);
    adminMode ? setNavAdmin(n) : setNavEmp(n);
    setMobileNavOpen(false);
    // Bumped on every sidebar click so views can reset transient sub-state
    // (e.g. the Employees directory snapping back to the Active tab) even when
    // the destination view key is unchanged and the component doesn't remount.
    setNavSignal((s) => s + 1);
  };
  const pendingCount = leaves.filter((l) => l.status === 'Pending HR').length; // HR's actionable queue

  return (
    <div
      className="flex app-fill w-full overflow-hidden"
      style={{ fontFamily: 'var(--app-font)', background: AG.bg, color: AG.text }}
    >
      {/* Sidebar slot — reserves 64px when collapsed so main content doesn't shift on hover (desktop only) */}
      {!isMobile && navCollapsed && <div className="ag-sidebar-slot" aria-hidden />}

      {/* Mobile drawer backdrop */}
      {isMobile && mobileNavOpen && (
        <div className="ag-sidebar-backdrop" onClick={() => setMobileNavOpen(false)} aria-hidden />
      )}

      {/* Sidebar — desktop: blend-in / collapsible rail. Mobile: off-canvas drawer. */}
      <aside
        className={`ag-sidebar flex flex-col ${isMobile ? 'mobile' : ''} ${isMobile && mobileNavOpen ? 'mobile-open' : ''} ${!isMobile && navCollapsed ? 'collapsed' : ''} ${!isMobile && sidebarFlyout ? 'hover-expanded' : ''}`}
        onMouseEnter={isMobile ? undefined : onSidebarEnter}
        onMouseLeave={isMobile ? undefined : onSidebarLeave}
      >
        <div className="ag-sidebar-brand">
          <img
            src={brandConfig.logo}
            alt={brandConfig.name}
            className={`ag-sidebar-logo${isDih ? ' dih' : ''}`}
          />
          <button
            type="button"
            className="ag-sidebar-toggle"
            onClick={() => {
              if (isMobile) { setMobileNavOpen(false); return; }
              setNavCollapsed((c) => !c);
              setNavHovered(false);
            }}
            aria-label={isMobile ? 'Close menu' : navCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <PanelLeft size={22} />
          </button>
        </div>

        <nav className="ag-sidebar-nav">
          <ul className="ag-nav-group">
            {groups.map((g, gi) => (
              <li
                key={gi}
                className={gi ? 'ag-nav-group' : ''}
                style={{ listStyle: 'none', marginTop: gi ? 16 : 0 }}
              >
                {g.label && <p className="ag-nav-section">{g.label}</p>}
                <ul className="ag-nav-items">
                  {g.items.map(({ name, label, icon: Icon }) => {
                    const on = active === name && !detail;
                    return (
                      <li key={name}>
                        <button
                          type="button"
                          onClick={() => setActive(name)}
                          className={`ag-nav-item ${on ? 'active' : ''}`}
                          title={navCollapsed && !navHovered ? (label || name) : undefined}
                        >
                          <Icon
                            size={20}
                            strokeWidth={on ? 2.25 : 2}
                            className="ag-nav-icon"
                          />
                          <span className="ag-nav-label">{label || name}</span>
                          {name === 'Leave' && pendingCount > 0 && (
                            <span
                              className="ag-nav-badge"
                              style={{
                                background: on
                                  ? AG.primary
                                  : AG.primaryLightest,
                                color: on ? '#fff' : AG.primary,
                              }}
                            >
                              {pendingCount}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="ag-top-header flex items-center gap-4 px-6 shrink-0">
          {isMobile && (
            <button
              type="button"
              className="ag-hamburger flex items-center justify-center"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation menu"
            >
              <MenuIcon size={22} />
            </button>
          )}
          <div ref={cmdWrapRef} className="flex-1 max-w-md" style={{ position: 'relative' }}>
            <div
              ref={searchBtnRef}
              className={`ag-search-trigger flex items-center gap-2.5${cmdOpen ? ' is-active' : ''}`}
              style={{ cursor: 'text' }}
              onClick={() => { setCmdOpen(true); cmdInputRef.current?.focus(); }}
            >
              <Search size={16} style={{ flexShrink: 0 }} />
              <input
                ref={cmdInputRef}
                value={cmdQ}
                onChange={(e) => { setCmdQ(e.target.value); if (!cmdOpen) setCmdOpen(true); }}
                onFocus={() => setCmdOpen(true)}
                onKeyDown={(e) => { if (e.key === 'Escape') closeCmd(); }}
                placeholder="Search or jump to..."
                style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: 'inherit', fontFamily: 'inherit' }}
              />
              {cmdOpen ? (
                <span onClick={(e) => { e.stopPropagation(); closeCmd(); }} style={{ fontSize: 11, background: '#fff', border: `1px solid ${AG.n200}`, borderRadius: 6, padding: '1px 6px', color: AG.n500, cursor: 'pointer', flexShrink: 0 }}>esc</span>
              ) : (
                <span style={{ fontSize: 11, background: '#fff', border: `1px solid ${AG.n200}`, borderRadius: 6, padding: '1px 6px', color: AG.n500, flexShrink: 0 }}>⌘K</span>
              )}
            </div>
            <CommandPalette
              open={cmdOpen}
              onClose={closeCmd}
              q={cmdQ}
              isHR={isHR}
              employees={employees}
              candidates={candidates}
              navTo={navTo}
              openEmployee={openEmployee}
              openCandidate={openCandidate}
              doAction={doAction}
              askAI={askAI}
            />
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {isHR && (
              <EntitySwitcher orgTree={orgTree} value={entityId} onChange={changeEntity} />
            )}
            {isHR ? (
              <div className="ag-ws-toggle" role="tablist" aria-label="Workspace mode">
                <button type="button" role="tab" aria-selected={!hrSelf}
                  className={`ag-ws-seg${!hrSelf ? ' on' : ''}`} onClick={() => switchWorkspace(false)}>
                  HR Admin
                </button>
                <button type="button" role="tab" aria-selected={hrSelf}
                  className={`ag-ws-seg${hrSelf ? ' on' : ''}`} onClick={() => switchWorkspace(true)}>
                  My Workspace
                </button>
              </div>
            ) : (
              <Pill tone="info" dot>{user?.entityName || 'Employee'}</Pill>
            )}
            {!isDih && (
              <button
                type="button"
                title="Settings"
                onClick={openDrawer}
                className="ag-header-icon-btn navlift flex items-center justify-center"
              >
                <SlidersHorizontal size={20} />
              </button>
            )}
            <NotificationBell onNavigate={(link) => { setDetail(null); navTo(link); }} />
            <AccountButton
              user={user}
              isHR={isHR}
              logout={logout}
              onSettings={() => isHR && navTo('Settings')}
            />
          </div>
        </header>

        <main
          className="ag-main flex-1 min-h-0 overflow-hidden flex flex-col"
        >
          {detail ? (
            detail.type === 'employee' ? (
              <EmployeeDetail
                id={detail.id}
                isHR={isHR}
                employees={employees}
                updateEmployee={updateEmployee}
                promoteToManager={promoteToManager}
                removeAsManager={removeAsManager}
                showToast={showToast}
                onBack={() => setDetail(null)}
              />
            ) : (
              <PageScroll>
                {detail.type === 'job' ? (
                  <JobDetail
                    id={detail.id}
                    jobs={jobs}
                    candidates={candidates}
                    advanceJob={advanceJob}
                    closeJob={closeJob}
                    openCandidate={openCandidate}
                    onBack={() => setDetail(null)}
                  />
                ) : (
                  <CandidateDetail
                    id={detail.id}
                    candidates={candidates}
                    jobs={jobs}
                    interviews={interviews}
                    offers={offers}
                    moveCandidate={moveCandidate}
                    rejectCandidate={rejectCandidate}
                    restoreCandidate={restoreCandidate}
                    createOffer={createOffer}
                    openJob={openJob}
                    onBack={() => setDetail(null)}
                  />
                )}
              </PageScroll>
            )
          ) : viewLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <CenterSpinner />
            </div>
          ) : adminMode ? (
            <AdminViews
              key={entityId}
              view={navAdmin}
              navSignal={navSignal}
              orgTree={orgTree}
              activeEntity={entityId}
              refreshOrg={refreshOrg}
              employees={employees}
              candidates={candidates}
              moveCandidate={moveCandidate}
              leaves={leaves}
              decideLeave={decideLeave}
              openEmployee={openEmployee}
              openCandidate={openCandidate}
              navTo={navTo}
              askAI={askAI}
              addEmployee={addEmployee}
              disableEmployee={disableEmployee}
              restoreEmployee={restoreEmployee}
              showToast={showToast}
              jobs={jobs}
              interviews={interviews}
              offers={offers}
              onboarding={onboarding}
              atsTab={atsTab}
              setAtsTab={setAtsTab}
              advanceJob={advanceJob}
              closeJob={closeJob}
              addJob={addJob}
              rejectCandidate={rejectCandidate}
              restoreCandidate={restoreCandidate}
              scheduleInterview={scheduleInterview}
              completeInterview={completeInterview}
              createOffer={createOffer}
              advanceOffer={advanceOffer}
              decideOffer={decideOffer}
              toggleOnboardTask={toggleOnboardTask}
              convertToEmployee={convertToEmployee}
              openJob={openJob}
            />
          ) : (
            <EmployeeViews
              key={entityId}
              view={navEmp}
              user={user}
              leaves={isHR ? leaves.filter((l) => l.empId === user?.id) : leaves}
              balances={balances}
              submitLeave={submitLeave}
              cancelLeave={cancelLeave}
              showToast={showToast}
            />
          )}
        </main>
      </div>

      <ChatWidget
        open={chatOpen}
        setOpen={setChatOpen}
        balances={balances}
        seed={chatSeed}
        clearSeed={() => setChatSeed(null)}
      />

      {toast &&
        (() => {
          const TONES = {
            success: { c: AG.success, Icon: Check },
            error: { c: AG.error, Icon: X },
            warning: { c: AG.warning, Icon: AlertTriangle },
            info: { c: AG.info, Icon: Sparkles },
          };
          const { c, Icon } = TONES[toast.type] || TONES.success;
          return (
            <div className="ag-snackbar" style={{ borderLeft: `4px solid ${c}` }}>
              <span className="ag-snackbar-icon" style={{ background: c }}>
                <Icon size={16} color="#fff" />
              </span>
              <span style={{ flex: 1, lineHeight: 1.4 }}>{toast.msg}</span>
            </div>
          );
        })()}
    </div>
  );
}

